import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Row, Col, Card, Statistic, Button, Progress } from 'antd';
import Icon from '@ant-design/icons';

import { ReactComponent as CO2Svg } from '../../static/icon_co2.svg';
import { ReactComponent as CoalSvg } from '../../static/icon_coal.svg';
import { ReactComponent as TreeSvg } from '../../static/icon_tree.svg';
import { ReactComponent as CertSvg } from '../../static/icon_cert.svg';
import { ReactComponent as SavingSvg } from '../../static/icon_saving.svg';
import { ReactComponent as DeviceSvg } from '../../static/device.svg';
import { ReactComponent as ParametersSvg } from '../../static/parameters.svg';
import { ReactComponent as FinanceSvg } from '../../static/finance.svg';
import { ReactComponent as AnalysisSvg } from '../../static/analysis.svg';
import { ReactComponent as MeterSvg } from '../../static/icons/energy_brought.svg';
import UpdateSiteParametersModal from './UpdateSiteParametersModal';
import MetersModal from './MetersModal';

import { Animated } from "react-animated-css";
import moment from 'moment';
import StationSelector from '../../components/dashboard/StationSelector';
import SummaryCards from '../../components/dashboard/SummaryCards';
import SiteInfoCard from '../../components/dashboard/SiteInfoCard';
import SiteChart from '../../components/dashboard/SiteChart';

import './Dashboard.scss';
import { initializeStation, formatSummary } from '../../utils/initStation';
import {
  getUserStationList,
  inspectionTimeUpdate,
  updateUserStation
} from '../../modules/analytics';

class Dashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      isSiteInfoModalVisible: false,
      isMetersModalVisible: false,
    };
  }

  async componentDidMount() {
    this.setState({ loading: true });
    await initializeStation(this.props);
    this.setState({ loading: false });
  }

  showSiteInfoModal = () => {
    this.setState({ isSiteInfoModalVisible: true });
  };

  hideSiteInfoModal = () => {
    this.setState({ isSiteInfoModalVisible: false });
  };

  showMetersModal = () => {
    this.setState({ isMetersModalVisible: true });
  };

  hideMetersModal = () => {
    this.setState({ isMetersModalVisible: false });
  };

  handleSiteInfoSubmit = async (values) => {
    try {
      await this.props.updateUserStation(values);
      const stations = await this.props.getUserStationList(true);
      const updated = stations.find(s => s.stationId === values.stationId);
      if (updated) {
        this.props.inspectionTimeUpdate({
          ...this.props.inspectionTime,
          stationId: updated.stationId,
          stationInfo: updated
        });
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
    }
    this.hideSiteInfoModal();
  };


  updateInspectionTime = (fields) => {
    const { inspectionTime } = this.props;
    let payload = { ...inspectionTime, ...fields };
    if (process.env.NODE_ENV === 'development') {
      console.log(payload);
    }
    this.props.inspectionTimeUpdate(payload);
  }

  getMomentUnit = (range) => ({ d: 'day', w: 'week', m: 'month', y: 'year' }[range] || 'day');

  getSummaryKey = (range) => ({ d: 'daily', w: 'weekly', m: 'monthly', y: 'yearly' }[range] || 'daily');

  getRangeLabel = (range) => ({ d: 'Daily', w: 'Weekly', m: 'Monthly', y: 'Annual' }[range] || 'Daily');

  getDateLabel = (dateMoment, range) => {
    switch (range) {
      case 'w': {
        const start = dateMoment.clone().startOf('week');
        const end = dateMoment.clone().endOf('week');
        return `${start.format('YYYY-MM-DD')} ~ ${end.format('MM-DD')}`;
      }
      case 'm':
        return dateMoment.format('YYYY-MM');
      case 'y':
        return dateMoment.format('YYYY');
      case 'd':
      default:
        return dateMoment.format('YYYY-MM-DD');
    }
  };

  render() {
    const { loading } = this.state;
    const { inspectionTime, stationSummary = {} } = this.props;
    const summary = stationSummary[inspectionTime.stationId] || {};
    const stats = formatSummary(summary, inspectionTime);
    const range = inspectionTime.range || 'd';
    const date = inspectionTime.date ? moment(inspectionTime.date) : moment();
    const unit = this.getMomentUnit(range);
    const summaryKey = this.getSummaryKey(range);
    const summaryLabel = this.getRangeLabel(range);
    const summaryStats = stats[summaryKey] || {};
    const siteInfo = inspectionTime.stationInfo || {};
    const siteCapacityKw = Number(siteInfo.capacity) || 0;
    const capacityMw = siteCapacityKw / 1000;
    const dailyEnergyKWh = Number(stats.daily?.value) || 0;
    const capacityFactor =
      siteCapacityKw > 0 ? (dailyEnergyKWh / (siteCapacityKw * 24)) * 100 : 0;
    const inverterData = Array.isArray(this.props.inverterData) ? this.props.inverterData : [];
    const totalGeneration = inverterData.reduce((sum, point) => {
      const energyValue =
        point && (point.energyKWh ?? point.energy ?? point.energy_kWh ?? point.energy_kwH);
      const value = Number(energyValue);
      if (Number.isNaN(value)) {
        return sum;
      }
      return sum + value;
    }, 0);
    const roundedGeneration = Number(totalGeneration.toFixed(3));
    const energyPrice = Number(siteInfo.estimatedSolarEnergyUnitPrice) || 0;
    const earningsValue = Number((totalGeneration * energyPrice).toFixed(2));
    if (process.env.NODE_ENV === 'development') {
      console.log({ siteInfo, stats });
    }
    return (
      <Animated animationIn="fadeIn">
        <div className="dashboardWrapper" style={{ paddingBottom: (this.props.play) ? '100px' : '20px' }}>

          {/* Summary and Sidebar */}
          <Row gutter={[16, 16]}>
            <Col xs={24} md={17} className='leftPanel'>
              <StationSelector pageTitle="Dashboard" />

              <SummaryCards stationId={inspectionTime.stationId} />

              <SiteChart
                range={range}
                dateLabel={this.getDateLabel(date, range)}
                onPrev={() => this.updateInspectionTime({
                  date: date.clone().subtract(1, unit).format('YYYY-MM-DD')
                })}
                onNext={() => this.updateInspectionTime({
                  date: date.clone().add(1, unit).format('YYYY-MM-DD')
                })}
                onRangeChange={(val) => this.updateInspectionTime({ range: val })}
                summaryLabel={summaryLabel}
                roundedGeneration={roundedGeneration}
                earningsValue={earningsValue}
                fullLoadHours={siteInfo.efficiency?.value === undefined ? 0 : summaryStats.change}
                dateValue={date}
              />
            </Col>
            <Col xs={24} md={7} className='rightPanel'>
              {/* Site Info */}
              <SiteInfoCard />

              {/* Efficiency */}
              <Card className="efficiencyCard">
                <div className="effLabel">Power Output Efficiency</div>
                <div className="efficiencyContent">
                  <div className="effBarWrapper">
                    <div className="efficiencyStats">
                      <span className="statValue">{dailyEnergyKWh}</span>
                      <span className="statSuffix">kWh</span>
                      <span className="divider">/</span>
                      <span className="statValue">{capacityMw.toFixed(3)}</span>
                      <span className="statSuffix"> MW Capacity</span>
                    </div>
                    <Progress
                      percent={capacityFactor}
                      showInfo={false}
                      size={8}
                      strokeColor={{
                        '0%': '#2F893D',
                        '100%': '#00B855',
                      }}
                      trailColor="#F0F0F0"
                    />
                  </div>
                  <div className="effPercent">{capacityFactor.toFixed(0)}%</div>
                </div>
              </Card>

              {/* CO2 and Coal Savings */}
              <div className="co2Cards">
                <Card className="smallCard"><Icon component={CO2Svg} /> <div className="innerCard"><span>CO2 Emission Saved</span><div><b>{(siteInfo.co2EmissionSavedRatio * stats.lifetime.value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</b>tons</div></div></Card>
                <Card className="smallCard"><Icon component={CoalSvg} /><div className="innerCard"><span>Equiv. Coal Saved</span><div><b>{(siteInfo.standardCoalSavedRatio * stats.lifetime.value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</b>tons</div></div></Card>
                <Card className="smallCard"><Icon component={TreeSvg} /><div className="innerCard"><span>Tree Plant</span><div><b>{(siteInfo.treePlantingRatio * stats.lifetime.value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</b></div></div></Card>
                <Card className="smallCard"><Icon component={CertSvg} /><div className="innerCard"><span>Equiv. Green Cert</span><div><b>{(siteInfo.equiGreenCertRatio * stats.lifetime.value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</b></div></div></Card>
              </div>

              {/* Savings summary */}
              <div className="co2Cards wide">
                <Card className="smallCard">
                  <Icon component={SavingSvg} />
                  <div className="innerCard first"><span>Savings (24h)</span><div>¥<b>{(siteInfo.estimatedSolarSavingsRatio * stats.daily.value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</b></div></div>
                  <div className="innerCard middle"><span>Savings (30d)</span><div>¥<b>{(siteInfo.estimatedSolarSavingsRatio * stats.monthly.value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</b></div></div>
                  <div className="innerCard last"><span>Savings (Total)</span><div>¥<b>{(siteInfo.estimatedSolarSavingsRatio * stats.lifetime.value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</b></div></div>
                </Card>
              </div>

              {/* Navigation Buttons */}
              <div className="navButtons">
                <Link to="/devices" className="navBtn ant-btn"><Icon component={DeviceSvg} /><div>Devices</div></Link>
                <Link to="/finance" className="navBtn ant-btn"><Icon component={FinanceSvg} /><div>Finance</div></Link>
                <Button className="navBtn" onClick={this.showMetersModal}>
                  <Icon component={MeterSvg} /><div>Meters</div>
                </Button>
                <Link to="/analysis" className="navBtn ant-btn"><Icon component={AnalysisSvg} /><div>Analysis</div></Link>
                <Button className="navBtn" onClick={this.showSiteInfoModal}>
                  <Icon component={ParametersSvg} /><div>Parameters</div>
                </Button>
              </div>
            </Col>
          </Row>

          {/* Site Info Parameters Modal */}
          <UpdateSiteParametersModal
            open={this.state.isSiteInfoModalVisible}
            onCancel={this.hideSiteInfoModal}
            onSubmit={this.handleSiteInfoSubmit}
            siteInfo={siteInfo}
          />
          <MetersModal
            open={this.state.isMetersModalVisible}
            onClose={this.hideMetersModal}
            stationId={inspectionTime.stationId}
          />
        </div>
      </Animated>
    );
  }


}

const mapStateToProps = state => ({
  router: state.router,
  userProfile: state.user.userProfile,
  userStationList: state.analytics.userStationList,
  inspectionTime: state.analytics.inspectionTime,
  stationSummary: state.analytics.stationSummary,
  inverterData: state.analytics.inverterData,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getUserStationList,
      inspectionTimeUpdate,
      updateUserStation
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
